import {State, Action, Selector, StateContext} from '@ngxs/store';
import {
  GetFavoriteVehiclesData,
  GetFavoriteVehiclesId,
  GetMakes,
  GetModelsData,
  SetFavoriteVehicle
} from './vehicle.actions';
import {VehicleService} from '@shared/service/vehicle/vehicle.service';
import {IVehicleMake, IVehicleModel} from '@shared/interfaces';
import {Injectable} from '@angular/core';
import {FavoriteVehicle} from '@shared/interfaces';
import {LoggerService} from '@app/service/logger/logger.service';

export interface VehicleStateModel {
  makes: IVehicleMake[];
  models: { [k: string]: IVehicleModel[] };
  favoriteVehiclesData: FavoriteVehicle[];
  favoriteVehiclesId: { [k: string]: boolean };
}

@State<VehicleStateModel>({
  name: 'vehicle',
  defaults: {
    makes: [],
    models: {},
    favoriteVehiclesData: [],
    favoriteVehiclesId: {}
  }
})
@Injectable()
export class VehicleState {
  constructor(private vehicleService: VehicleService, private logger: LoggerService) {
  }

  @Selector()
  static getMakes(state: VehicleStateModel) {
    return state.makes;
  }

  @Selector()
  static getModels(state: VehicleStateModel) {
    return state.models;
  }

  @Selector()
  static getFavoriteVehiclesData(state: VehicleStateModel) {
    return state.favoriteVehiclesData;
  }

  @Selector()
  static getFavoriteVehiclesId(state: VehicleStateModel) {
    return state.favoriteVehiclesId;
  }

  @Action(GetMakes)
  getMakesData({getState, patchState}: StateContext<VehicleStateModel>) {
    // We want to get all the makes only once
    const curMakes = getState().makes;
    if (curMakes && curMakes.length > 0) {
      return Promise.resolve(true);
    }
    return this.vehicleService.getMakes()
      .then((result) => {
        patchState({
          makes: result
        });
      });
  }

  @Action(GetModelsData)
  getModelsData({getState, patchState}: StateContext<VehicleStateModel>, {payload}: GetModelsData) {
    // We want to get all the makes only once
    let models = getState().models;
    const make = payload.make;
    const curModelsByMake = models[make];
    if (curModelsByMake && curModelsByMake.length > 0) {
      return Promise.resolve(true);
    }
    return this.vehicleService.getModelsByMake(make)
      .then((result) => {
        models = Object.assign({}, models, {[make]: result});
        patchState({
          models
        });
      });
  }

  @Action(GetFavoriteVehiclesData)
  _getFavoriteVehiclesData(sc: StateContext<VehicleStateModel>) {
    // We want to get all the makes only once
    const favVehicleData = sc.getState().favoriteVehiclesData;
    const favVehicleId = sc.getState().favoriteVehiclesId;
    // Use the ids as a way to make sure the fav vehicle data is always up to date by checking for mismatches in count
    if (favVehicleData && favVehicleData.length > 0 && favVehicleData.length === Object.keys(favVehicleId).length) {
      return Promise.resolve(true);
    }
    return this.vehicleService.getFavoriteVehiclesData()
      .then((result: FavoriteVehicle[]) => {
        sc.patchState({
          favoriteVehiclesData: result || []
        });
      });
  }

  @Action(GetFavoriteVehiclesId)
  _getFavoriteVehiclesId(sc: StateContext<VehicleStateModel>) {
    const favVehicleData = sc.getState().favoriteVehiclesData;
    const favVehicleId = sc.getState().favoriteVehiclesId;
    // Use the ids as a way to make sure the fav vehicle data is always up to date by checking for mismatches in count
    if (favVehicleData && favVehicleData.length > 0 && favVehicleData.length === Object.keys(favVehicleId).length) {
      return Promise.resolve(true);
    }
    // We want to get all the fav vehicles only once
    return this.vehicleService.getFavoriteVehiclesId()
      .then((result) => {
        sc.patchState({
          favoriteVehiclesId: result || {}
        });
      });
  }

  @Action(SetFavoriteVehicle)
  _setFavoriteVehicle(sc: StateContext<VehicleStateModel>, {payload}: SetFavoriteVehicle) {
    const vehicleId = payload.vehicleId;
    const vehicleData = payload.vehicleData;
    const action = payload.action;
    return this.vehicleService.setFavoriteVehicle(vehicleId, vehicleData, action)
      .then(() => {
        const favoriteVehiclesId = Object.assign({}, sc.getState().favoriteVehiclesId);
        let favoriteVehiclesData = Object.assign([], sc.getState().favoriteVehiclesData);
        if (action === 'delete') {
          favoriteVehiclesId[vehicleId] = false;
          if (favoriteVehiclesData && Object.keys(favoriteVehiclesData).length > 0) {
            favoriteVehiclesData = favoriteVehiclesData.filter(data => data.id !== vehicleId);
          }
        } else if (action === 'save') {
          favoriteVehiclesId[vehicleId] = true;
          if (!favoriteVehiclesData) {
            favoriteVehiclesData = [];
          }
          favoriteVehiclesData = favoriteVehiclesData.concat([{...vehicleData, id: vehicleId}]);
        }
        return sc.patchState({
          favoriteVehiclesId,
          favoriteVehiclesData
        });
      })
      .catch(err => {
        this.logger.error(`Set Favorite Error - ${err}`);
      });
  }
}
